import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';

import { Card } from '@/components/ui/card';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PercentageInput } from '@/components/ui/percentage-input';

import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { CampaignAutocomplete } from '@/components/ui/campaign-autocomplete';
import { CustomerAutocomplete } from '@/components/ui/customer-autocomplete';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import { TariffAutocomplete } from '@/components/ui/tariff-autocomplete';
import {
	useEntityForm,
	type EntityFormProps,
	type EntityFormSubmitHandler
} from '@/hooks/useEntityForm';
import { type VoucherDiscountTypeOption } from './server-data-hooks';

const createVoucherFormSchema = (t: (key: string) => string) =>
	z
		.object({
			campaignId: z.string().optional(),
			name: z.string().optional(),
			code: z
				.string({
					required_error: t('vouchers:validation.code.required')
				})
				.trim()
				.min(1, t('vouchers:validation.code.minLength')),
			customerId: z.string().optional(),
			tariffId: z.string().optional(),
			voucherDiscountTypeId: z
				.string({
					required_error: t(
						'vouchers:validation.voucherDiscountType.required'
					)
				})
				.trim()
				.min(1, t('vouchers:validation.voucherDiscountType.required')),
			discountValue: z
				.string()
				.optional()
				.transform(value => (value ?? '').trim())
				.refine(
					value => {
						if (value === '') {
							return true;
						}
						const numeric = Number(value);
						return !Number.isNaN(numeric) && numeric >= 0;
					},
					{
						message: t(
							'vouchers:validation.discountValue.invalidFormat'
						)
					}
				),
			maxDiscountAmount: z
				.string()
				.optional()
				.transform(value => (value ?? '').trim())
				.refine(
					value => {
						if (value === '') {
							return true;
						}
						const numeric = Number(value);
						return !Number.isNaN(numeric) && numeric >= 0;
					},
					{
						message: t(
							'vouchers:validation.maxDiscountAmount.invalidFormat'
						)
					}
				),
			usageLimit: z
				.string()
				.optional()
				.transform(value => (value ?? '').trim())
				.refine(
					value => {
						if (value === '') {
							return true;
						}
						const numeric = Number(value);
						return Number.isInteger(numeric) && numeric >= 0;
					},
					{ message: t('vouchers:validation.usageLimit.integer') }
				),
			isActive: z.boolean(),
			validUntilDate: z
				.string({
					required_error: t(
						'vouchers:validation.validUntilDate.required'
					)
				})
				.trim()
				.min(1, t('vouchers:validation.validUntilDate.required'))
				.refine(value => !Number.isNaN(new Date(value).getTime()), {
					message: t('vouchers:validation.validUntilDate.invalid')
				})
		})
		.superRefine((values, ctx) => {
			// Campaign and customer are mutually exclusive
			const hasCampaign = values.campaignId?.trim();
			const hasCustomer = values.customerId?.trim();

			if (hasCampaign && hasCustomer) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['campaignId'],
					message: t('vouchers:validation.campaignCustomerExclusive')
				});
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['customerId'],
					message: t('vouchers:validation.campaignCustomerExclusive')
				});
			}

			// If no campaign or customer is selected, a display name is required
			if (
				!hasCampaign &&
				!hasCustomer &&
				!(values.name ?? '').trim()
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['name'],
					message: t('vouchers:validation.name.required')
				});
			}

			// Conditional validation based on discount type
			const discountType = values.voucherDiscountTypeId;

			if (discountType === '1') {
				// Tariff replacement - require tariff
				if (!values.tariffId?.trim()) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						path: ['tariffId'],
						message: t('vouchers:validation.tariff.required')
					});
				}
			} else if (discountType === '2') {
				// Fixed amount - require discount value (currency)
				if (!values.discountValue?.trim()) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						path: ['discountValue'],
						message: t('vouchers:validation.discountValue.required')
					});
				} else {
					const numericValue = Number(values.discountValue);
					if (Number.isNaN(numericValue) || numericValue <= 0) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							path: ['discountValue'],
							message: t(
								'vouchers:validation.discountValue.positive'
							)
						});
					}
				}
			} else if (discountType === '3') {
				// Percentage - require discount value (percentage) and max discount amount (currency)
				if (!values.discountValue?.trim()) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						path: ['discountValue'],
						message: t('vouchers:validation.discountValue.required')
					});
				} else {
					const numericValue = Number(values.discountValue);
					if (Number.isNaN(numericValue) || numericValue <= 0) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							path: ['discountValue'],
							message: t(
								'vouchers:validation.discountValue.positive'
							)
						});
					} else if (numericValue > 100) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							path: ['discountValue'],
							message: t(
								'vouchers:validation.discountValue.percentageMax'
							)
						});
					}
				}

				// MaxDiscountAmount is only required for percentage type
				if (values.maxDiscountAmount?.trim()) {
					const numericValue = Number(values.maxDiscountAmount);
					if (Number.isNaN(numericValue) || numericValue <= 0) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							path: ['maxDiscountAmount'],
							message: t(
								'vouchers:validation.maxDiscountAmount.positive'
							)
						});
					}
				}
			}
		});

export type VoucherFormValues = z.infer<
	ReturnType<typeof createVoucherFormSchema>
>;

export interface VoucherFormSubmitData {
	voucherId?: string;
	campaignId: string | null;
	name: string | null;
	code: string;
	isAdHoc: boolean;
	isCustomerSpecific: boolean;
	customerId: string | null;
	tariffBindingRequired: boolean;
	tariffId: string | null;
	voucherDiscountTypeId: number;
	discountValue: number | null;
	maxDiscountAmount: number | null;
	usageLimit: number | null;
	isActive: boolean;
	validUntilDate: string;
	// Display data for autocomplete initial labels
	campaign?: { campaignId: string; name: string } | null;
	customer?: { customerId: string; firmName?: string | null; email?: string | null } | null;
	tariff?: { tariffId: string; name?: string | null } | null;
}

export interface VoucherFormProps
	extends Omit<
		EntityFormProps<VoucherFormValues, VoucherFormSubmitData>,
		'onSubmit' | 'initialData'
	> {
	voucherDiscountTypeOptions: VoucherDiscountTypeOption[];
	initialData?: VoucherFormSubmitData;
	onSubmit: EntityFormSubmitHandler<VoucherFormSubmitData>;
	headerTitle: string;
	onBack?: () => void;
}

const toLocalInputValue = (isoValue?: string | null) => {
	if (!isoValue) {
		return '';
	}

	const date = new Date(isoValue);
	if (Number.isNaN(date.getTime())) {
		return '';
	}

	const tzOffset = date.getTimezoneOffset() * 60000;
	const local = new Date(date.getTime() - tzOffset);
	return local.toISOString().slice(0, 16);
};

const toIsoString = (value: string) => {
	if (!value) {
		return '';
	}

	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const buildInitialValues = (
	data?: VoucherFormSubmitData
): VoucherFormValues | undefined => {
	if (!data) {
		return undefined;
	}

	return {
		campaignId: data.campaignId ?? '',
		name: data.name ?? '',
		code: data.code ?? '',
		customerId: data.customerId ?? '',
		tariffId: data.tariffId ?? '',
		voucherDiscountTypeId: String(data.voucherDiscountTypeId),
		discountValue:
			data.discountValue != null ? String(data.discountValue) : '',
		maxDiscountAmount:
			data.maxDiscountAmount != null
				? String(data.maxDiscountAmount)
				: '',
		usageLimit: data.usageLimit != null ? String(data.usageLimit) : '',
		isActive: data.isActive,
		validUntilDate: toLocalInputValue(data.validUntilDate)
	};
};

const parseOptionalNumber = (value: string): number | null => {
	const trimmed = value.trim();
	return trimmed === '' ? null : Number(trimmed);
};

export const VoucherForm = ({
	mode,
	initialData,
	voucherDiscountTypeOptions,
	onSubmit,
	isSubmitting,
	headerTitle,
	onBack
}: VoucherFormProps) => {
	const { t } = useTranslation();
	const codeInputRef = useRef<HTMLInputElement>(null);

	const voucherFormSchema = useMemo(() => createVoucherFormSchema(t), [t]);

	const memoizedInitialData = useMemo(
		() => buildInitialValues(initialData),
		[initialData]
	);

	// Default to 30 days from now for new vouchers
	const defaultValidUntilDate = useMemo(() => {
		const future = new Date();
		future.setDate(future.getDate() + 30);
		// Format for datetime-local input (YYYY-MM-DDTHH:MM)
		return future.toISOString().slice(0, 16);
	}, []);

	const form = useEntityForm<VoucherFormValues>({
		mode,
		schema: voucherFormSchema,
		defaultValues: {
			campaignId: '',
			name: '',
			code: '',
			customerId: '',
			tariffId: '',
			voucherDiscountTypeId:
				voucherDiscountTypeOptions.length > 0
					? String(
							voucherDiscountTypeOptions[0].voucherDiscountTypeId
						)
					: '',
			discountValue: '',
			maxDiscountAmount: '',
			usageLimit: '',
			isActive: true,
			validUntilDate: mode === 'create' ? defaultValidUntilDate : ''
		},
		initialData: memoizedInitialData
	});

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	// Auto-focus code field when creating a new voucher
	useEffect(() => {
		if (mode === 'create') {
			setTimeout(() => {
				codeInputRef.current?.focus();
			}, 0);
		}
	}, [mode]);

	const watchedValues = form.watch();

	// Set default voucher discount type when options load
	useEffect(() => {
		if (
			mode === 'create' &&
			voucherDiscountTypeOptions.length > 0 &&
			!watchedValues.voucherDiscountTypeId
		) {
			form.setValue(
				'voucherDiscountTypeId',
				String(voucherDiscountTypeOptions[0].voucherDiscountTypeId)
			);
		}
	}, [
		voucherDiscountTypeOptions,
		mode,
		watchedValues.voucherDiscountTypeId,
		form
	]);

	const handleFormSubmit: EntityFormSubmitHandler<
		VoucherFormValues
	> = values => {
		const campaignId = values.campaignId?.trim() || null;
		const customerId = values.customerId?.trim() || null;
		const tariffId = values.tariffId?.trim() || null;

		const submitData: VoucherFormSubmitData = {
			campaignId,
			name: values.name?.trim() ? values.name.trim() : null,
			code: values.code.trim(),
			// Derived: if neither campaign nor customer, it's ad-hoc
			isAdHoc: !campaignId && !customerId,
			// Derived: if customerId provided, it's customer-specific
			isCustomerSpecific: Boolean(customerId),
			customerId,
			// Derived: if tariffId provided, tariff binding is required
			tariffBindingRequired: Boolean(tariffId),
			tariffId,
			voucherDiscountTypeId: Number(values.voucherDiscountTypeId),
			discountValue: parseOptionalNumber(values.discountValue ?? ''),
			maxDiscountAmount: parseOptionalNumber(
				values.maxDiscountAmount ?? ''
			),
			usageLimit: parseOptionalNumber(values.usageLimit ?? ''),
			isActive: values.isActive,
			validUntilDate: toIsoString(values.validUntilDate)
		};

		onSubmit(submitData);
	};

	const selectedDiscountType = voucherDiscountTypeOptions.find(
		option =>
			String(option.voucherDiscountTypeId) ===
			watchedValues.voucherDiscountTypeId
	);

	// Fixed amount (2) - show currency input for discount value
	const isFixedAmountType = selectedDiscountType?.voucherDiscountTypeId === 2;

	// Percentage (3) - show percentage input for discount value AND max discount amount
	const isPercentageType = selectedDiscountType?.voucherDiscountTypeId === 3;

	// Show discount value field for both fixed amount and percentage types
	const showDiscountValueField = isFixedAmountType || isPercentageType;

	// MaxDiscountAmount is only visible for percentage type
	const showMaxDiscountField = isPercentageType;

	// Tariff field only for tariff replacement type (1)
	const showTariffField = selectedDiscountType?.voucherDiscountTypeId === 1;

			return (
				<>
					<FormHeaderWithStatus
						title={headerTitle}
						onBack={onBack}
						control={form.control}
						rightContent={
							<FormActionButtons
								mode={mode}
								isSubmitting={isSubmitting}
								validationErrors={form.formState.errors}
								formId="voucher-form"
								onScrollToError={() => {
									const firstErrorField = document.querySelector(
										'[aria-invalid="true"]'
									) as HTMLElement;
									if (firstErrorField) {
										firstErrorField.scrollIntoView({
											behavior: 'smooth',
											block: 'center'
										});
										setTimeout(() => firstErrorField.focus(), 300);
									}
								}}
							/>
						}
					/>			<Form {...form}>
			<form
				id="voucher-form"
				onSubmit={form.handleSubmit(handleFormSubmit)}
				className="space-y-6"
			>
				{/* Basic Information Section */}
				<Card className="space-y-4">
					<h3 className="text-lg font-semibold">
						{t('vouchers:sections.basic')}
					</h3>

					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<FormField
							control={form.control}
							name="code"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('vouchers:fields.code.label')}
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											ref={codeInputRef}
											placeholder={t(
												'vouchers:fields.code.placeholder'
											)}
										/>
									</FormControl>
									<FormDescription>
										{t('vouchers:fields.code.description')}
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('vouchers:fields.name.label')}
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder={t(
												'vouchers:fields.name.placeholder'
											)}
										/>
									</FormControl>
									<FormDescription>
										{t('vouchers:fields.name.description')}
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</Card>

				{/* Campaign & Customer Assignment Section */}
				<Card className="space-y-4">
					<h3 className="text-lg font-semibold">
						{t('vouchers:sections.assignment')}
					</h3>

					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<FormField
							control={form.control}
							name="campaignId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('vouchers:fields.campaign.label')}
									</FormLabel>
									<FormControl>
										<CampaignAutocomplete
											value={field.value || undefined}
											onChange={(campaignId: string) => {
												field.onChange(campaignId || '');
												// Trigger validation for both fields to clear errors
												form.trigger(['campaignId', 'customerId']);
											}}
											placeholder={t(
												'vouchers:fields.campaign.placeholder'
											)}
											initialSelectedLabel={initialData?.campaign?.name}
										/>
									</FormControl>
									<FormDescription>
										{t(
											'vouchers:fields.campaign.description'
										)}
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="customerId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('vouchers:fields.customer.label')}
									</FormLabel>
									<FormControl>
										<CustomerAutocomplete
											value={field.value || undefined}
											onChange={(customerId: string) => {
												field.onChange(customerId || '');
												// Trigger validation for both fields to clear errors
												form.trigger(['campaignId', 'customerId']);
											}}
											placeholder={t(
												'vouchers:fields.customer.placeholder'
											)}
											initialSelectedLabel={
												initialData?.customer?.firmName ||
												initialData?.customer?.email ||
												undefined
											}
										/>
									</FormControl>
									<FormDescription>
										{t(
											'vouchers:fields.customer.description'
										)}
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</Card>

				{/* Discount Configuration Section */}
				<Card className="space-y-4">
					<h3 className="text-lg font-semibold">
						{t('vouchers:sections.discount')}
					</h3>{' '}
					<FormField
						control={form.control}
						name="voucherDiscountTypeId"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t(
										'vouchers:fields.voucherDiscountType.label'
									)}
								</FormLabel>
								<FormControl>
									<SentryDropdown
										value={field.value}
										onChange={field.onChange}
										placeholder={t(
											'vouchers:fields.voucherDiscountType.placeholder'
										)}
										options={voucherDiscountTypeOptions.map(
											type => ({
												value: String(
													type.voucherDiscountTypeId
												),
												label: type.name
											})
										)}
									/>
								</FormControl>
								<FormDescription>
									{t(
										'vouchers:fields.voucherDiscountType.description'
									)}
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>
					{showTariffField && (
						<FormField
							control={form.control}
							name="tariffId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('vouchers:fields.tariff.label')}
									</FormLabel>
									<FormControl>
										<TariffAutocomplete
											value={field.value || ''}
											onChange={(tariffId: string) =>
												field.onChange(tariffId || '')
											}
											placeholder={t(
												'vouchers:fields.tariff.placeholder'
											)}
											initialSelectedLabel={initialData?.tariff?.name || undefined}
										/>
									</FormControl>
									<FormDescription>
										{t(
											'vouchers:fields.tariff.description'
										)}
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}
					{showDiscountValueField && (
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<FormField
								control={form.control}
								name="discountValue"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{isPercentageType
												? t('vouchers:fields.discountValue.labelPercentage')
												: t('vouchers:fields.discountValue.labelCurrency')}
										</FormLabel>
										<FormControl>
											{isPercentageType ? (
												<PercentageInput
													{...field}
													placeholder={t(
														'vouchers:fields.discountValue.placeholderPercentage'
													)}
												/>
											) : (
												<CurrencyInput
													{...field}
													placeholder={t(
														'vouchers:fields.discountValue.placeholderCurrency'
													)}
												/>
											)}
										</FormControl>
										<FormDescription>
											{isPercentageType
												? t('vouchers:fields.discountValue.descriptionPercentage')
												: t('vouchers:fields.discountValue.descriptionCurrency')}
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>

							{showMaxDiscountField && (
								<FormField
									control={form.control}
									name="maxDiscountAmount"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t(
													'vouchers:fields.maxDiscountAmount.label'
												)}
											</FormLabel>
											<FormControl>
												<CurrencyInput
													{...field}
													placeholder={t(
														'vouchers:fields.maxDiscountAmount.placeholder'
													)}
												/>
											</FormControl>
											<FormDescription>
												{t(
													'vouchers:fields.maxDiscountAmount.description'
												)}
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}
						</div>
					)}
				</Card>

				{/* Usage Restrictions Section */}
				<Card className="space-y-4">
					<h3 className="text-lg font-semibold">
						{t('vouchers:sections.restrictions')}
					</h3>{' '}
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<FormField
							control={form.control}
							name="usageLimit"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('vouchers:fields.usageLimit.label')}
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											type="number"
											min="0"
											placeholder={t(
												'vouchers:fields.usageLimit.placeholder'
											)}
										/>
									</FormControl>
									<FormDescription>
										{t(
											'vouchers:fields.usageLimit.description'
										)}
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="validUntilDate"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t(
											'vouchers:fields.validUntilDate.label'
										)}
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											type="datetime-local"
											placeholder={t(
												'vouchers:fields.validUntilDate.placeholder'
											)}
										/>
									</FormControl>
									<FormDescription>
										{t(
											'vouchers:fields.validUntilDate.description'
										)}
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</Card>
			</form>
		</Form>
		</>
	);
};
